import { useState, useRef, useEffect } from "react";
import {
  FiSearch,
  FiChevronDown,
  FiUploadCloud,
  FiPaperclip,
  FiX,
  FiDownload,
  FiLoader,
} from "react-icons/fi";
import {
  useSupportTickets,
  useCreateSupportTicket,
  useSupportTicketById,
} from "../../custom-hooks/queries";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { uploadFile } from "../../utils/HelperMethod";
import { useAuthStore } from "../../store/useAuthStore";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";

export default function SupportCenter() {
  // Pagination and filters state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState("Newest");
  const role = useAuthStore((state) => state.role);

  // Debounce search
  const searchTimeoutRef = useRef(null);
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [search]);

  // Fetch tickets
  const { data: ticketsData, isLoading: ticketsLoading } = useSupportTickets({
    page,
    pageSize,
    status: selectedStatus,
    search: debouncedSearch,
    sort: selectedSort,
    role: role.toLowerCase(),
  });

  const tickets = ticketsData?.data?.data || [];
  const pagination = {
    currentPage: ticketsData?.data?.currentPage || page,
    totalPages: ticketsData?.data?.totalPages || 1,
    totalItems: ticketsData?.data?.totalItems || 0,
  };

  // Create ticket form state
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Technical Support");
  const [selectedPriority, setSelectedPriority] = useState("Normal");
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Dropdowns state
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const categories = [
    "Technical Support",
    "Billing",
    "Payments",
    "General Inquiry",
  ];
  const priorities = ["Low", "Normal", "High", "Urgent"];
  const statusOptions = [
    "All",
    "Open",
    "Pending",
    "Resolved",
    "Closed",
    "Urgent",
  ];
  const sortOptions = ["Newest", "Oldest"];

  // Ticket detail modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const createTicketMutation = useCreateSupportTicket();
  const getTicketDetailMutation = useSupportTicketById();

  // File upload loading state
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // File Upload
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Create ticket handler
  const handleSubmitTicket = async (e) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setIsUploadingFiles(true);

      // Upload all files and collect URLs
      const attachmentUrls = [];

      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const uploadedUrl = await uploadFile(file, "supportAttachment");
          if (uploadedUrl) {
            // Remove the SAS token from the URL for storage
            const cleanUrl = uploadedUrl.split("?")[0];
            attachmentUrls.push(cleanUrl);
          }
        }
      }

      setIsUploadingFiles(false);

      createTicketMutation.mutate(
        {
          role,
          data: {
            subject,
            category: selectedCategory,
            description,
            priority: selectedPriority,
            attachmentUrls,
          },
        },
        {
          onSuccess: () => {
            toast.success("Ticket created successfully!");

            // Reset form
            setSubject("");
            setDescription("");
            setSelectedCategory("Technical Support");
            setSelectedPriority("Normal");
            setSelectedFiles([]);

            // Refetch tickets
            queryClient.invalidateQueries(["supportTickets"]);
          },
          onError: (error) => {
            toast.error(
              error?.response?.data?.message || "Failed to create ticket"
            );
          },
        }
      );
    } catch (error) {
      setIsUploadingFiles(false);
      toast.error("Failed to upload attachments. Please try again.");
      console.error("File upload error:", error);
    }
  };

  // View ticket detail handler
  const handleViewTicket = (ticketId) => {
    navigate(`/${role.toLowerCase()}/support-details/${ticketId}`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="w-full px-4 py-1 sm:px-6 sm:py-1">
      {/* PAGE TITLE */}
      <PageHeader
        title={"Support center"}
        description={"Manage your support tickets or create a new one."}
      />

      <div className="mt-6 sm:mt-10 grid grid-cols-1 lg:grid-cols-[1.1fr,0.9fr] gap-6 sm:gap-10">
        {/* LEFT SIDE - TICKETS LIST */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            My Tickets
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            Track your open and resolved support requests.
          </p>

          {/* SEARCH + FILTERS */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4 sm:mt-5">
            {/* SEARCH BAR */}
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white shadow-sm w-full">
              <FiSearch className="text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by keyword or Ticket ID..."
                className="w-full text-sm outline-none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* STATUS DROPDOWN */}
            <Dropdown
              label={selectedStatus === "All" ? "All Statuses" : selectedStatus}
              open={statusOpen}
              setOpen={setStatusOpen}
              options={statusOptions}
              onSelect={(val) => {
                setSelectedStatus(val);
                setPage(1);
              }}
            />

            {/* SORT DROPDOWN */}
            <Dropdown
              label={
                selectedSort === "Newest" ? "Newest First" : "Oldest First"
              }
              open={sortOpen}
              setOpen={setSortOpen}
              options={sortOptions}
              onSelect={(val) => {
                setSelectedSort(val);
                setPage(1);
              }}
              displayMap={{ Newest: "Newest First", Oldest: "Oldest First" }}
            />
          </div>

          {/* TICKETS LIST */}
          <div className="mt-4 sm:mt-6 space-y-3">
            {ticketsLoading ? (
              // Skeleton loading
              [...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="border rounded-xl p-3 sm:p-4 bg-white shadow-sm animate-pulse"
                >
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-0">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                      <div className="h-4 bg-gray-200 rounded w-48"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : tickets.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No tickets found
              </div>
            ) : (
              tickets.map((t) => (
                <TicketCard
                  key={t.ticketId}
                  ticket={t}
                  formatDate={formatDate}
                  onView={() => handleViewTicket(t.ticketId)}
                  isLoading={
                    getTicketDetailMutation.isPending &&
                    getTicketDetailMutation.variables === t.ticketId
                  }
                />
              ))
            )}
          </div>

          {/* PAGINATION */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!pagination.hasPrevious}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!pagination.hasNext}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* RIGHT — CREATE NEW TICKET */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 sm:p-6 shadow-sm h-fit">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            Create a New Ticket
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            Need help? Submit a support ticket and our team will get back to
            you.
          </p>

          <form
            onSubmit={handleSubmitTicket}
            className="mt-4 sm:mt-6 space-y-4"
          >
            {/* SUBJECT */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter issue title"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#01B9C6]"
              />
            </div>

            {/* CATEGORY */}
            <div className="relative">
              <label className="text-sm font-medium text-gray-700">
                Category
              </label>

              <div
                className="mt-1 w-full border rounded-lg px-3 py-2.5 bg-white flex items-center justify-between cursor-pointer"
                onClick={() => setCategoryOpen(!categoryOpen)}
              >
                <span className="text-sm">{selectedCategory}</span>
                <FiChevronDown size={16} className="text-gray-500" />
              </div>

              {categoryOpen && (
                <div className="mt-1 border rounded-lg bg-white shadow-md w-full absolute z-20">
                  {categories.map((cat) => (
                    <div
                      key={cat}
                      onClick={() => {
                        setSelectedCategory(cat);
                        setCategoryOpen(false);
                      }}
                      className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer w-full"
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PRIORITY */}
            <div className="relative">
              <label className="text-sm font-medium text-gray-700">
                Priority
              </label>

              <div
                className="mt-1 w-full border rounded-lg px-3 py-2.5 bg-white flex items-center justify-between cursor-pointer"
                onClick={() => setPriorityOpen(!priorityOpen)}
              >
                <span className="text-sm">{selectedPriority}</span>
                <FiChevronDown size={16} className="text-gray-500" />
              </div>

              {priorityOpen && (
                <div className="mt-1 border rounded-lg bg-white shadow-md w-full absolute z-20">
                  {priorities.map((priority) => (
                    <div
                      key={priority}
                      onClick={() => {
                        setSelectedPriority(priority);
                        setPriorityOpen(false);
                      }}
                      className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer w-full"
                    >
                      {priority}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* DESCRIPTION */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Please describe your issue in detail..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2.5 h-28 text-sm outline-none focus:border-[#01B9C6] resize-none"
              />
            </div>

            {/* FILE UPLOAD */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Attachments (Optional)
              </label>

              <label className="mt-2 border border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center h-32 gap-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition">
                <FiUploadCloud size={28} className="text-gray-400" />
                <p className="text-sm text-gray-700">Drag & drop files here</p>
                <p className="text-xs text-gray-500">or click to browse</p>

                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  multiple
                />
              </label>

              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-sm flex-1 min-w-0">
                        <FiPaperclip className="text-[#01B9C6] w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={createTicketMutation.isPending || isUploadingFiles}
              className="w-full bg-[#01B9C6] hover:bg-[#00A8B4] text-white text-sm font-medium py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(isUploadingFiles || createTicketMutation.isPending) && (
                <FiLoader className="animate-spin" size={16} />
              )}
              {isUploadingFiles
                ? "Uploading files..."
                : createTicketMutation.isPending
                ? "Submitting..."
                : "Submit Ticket"}
            </button>
          </form>
        </div>
      </div>

      {/* TICKET DETAIL MODAL */}
      {showTicketModal && selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket?.data}
          onClose={() => {
            setShowTicketModal(false);
            setSelectedTicket(null);
          }}
          formatDateTime={formatDateTime}
        />
      )}
    </div>
  );
}

/* ----------------------------- DROPDOWN ----------------------------- */

function Dropdown({ label, open, setOpen, options, onSelect, displayMap }) {
  return (
    <div className="relative w-full sm:w-[180px]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full border rounded-lg px-3 py-2 bg-white shadow-sm cursor-pointer text-sm text-gray-700"
      >
        <span className="truncate">{displayMap?.[label] || label}</span>
        <FiChevronDown size={16} className="text-gray-500 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute w-full sm:w-40 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-20">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => {
                onSelect(opt);
                setOpen(false);
              }}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
            >
              {displayMap?.[opt] || opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- TICKET CARD ----------------------------- */

function TicketCard({ ticket, formatDate, onView, isLoading }) {
  const statusColors = {
    Open: "bg-[#01B9C6]/20 text-[#01B9C6]",
    Resolved: "bg-green-100 text-green-600",
    Pending: "bg-yellow-100 text-yellow-600",
    Urgent: "bg-red-100 text-red-600",
    Closed: "bg-gray-100 text-gray-600",
  };

  return (
    <div
      className="
    border rounded-xl
    p-3 sm:p-4
    bg-white shadow-sm
    flex flex-col sm:flex-row
    items-start sm:items-center
    justify-between
    gap-3 sm:gap-0
  "
    >
      {/* LEFT INFO */}
      <div className="flex-1 min-w-0">
        <p className="text-xs sm:text-sm font-semibold text-gray-900">
          #{ticket.ticketCode}
        </p>

        <p className="text-gray-800 text-xs sm:text-sm mt-1 break-words">
          {ticket.subject}
        </p>

        <p className="text-gray-500 text-xs mt-1">
          Opened on {formatDate(ticket.createdAt)}
        </p>

        {ticket.category && (
          <p className="text-gray-400 text-xs mt-0.5">{ticket.category}</p>
        )}
      </div>

      {/* RIGHT ACTIONS */}
      <div
        className="
      flex flex-row sm:flex-col
      items-center sm:items-end
      justify-between sm:justify-start
      gap-2
      w-full sm:w-auto
    "
      >
        <span
          className={`text-xs px-2 sm:px-3 py-1 rounded-full font-semibold whitespace-nowrap ${
            statusColors[ticket.status] || "bg-gray-100 text-gray-600"
          }`}
        >
          {ticket.status}
        </span>

        <button
          onClick={onView}
          disabled={isLoading}
          className="
        text-[#01B9C6]
        text-xs sm:text-sm
        font-medium
        flex items-center gap-1
        hover:underline
        whitespace-nowrap
        disabled:opacity-50
        mt-0 sm:mt-2
      "
        >
          {isLoading ? "Loading..." : "View Ticket →"}
        </button>
      </div>
    </div>
  );
}

/* ----------------------------- TICKET DETAIL MODAL ----------------------------- */

function TicketDetailModal({ ticket, onClose, formatDateTime }) {
  const statusColors = {
    Open: "bg-[#01B9C6]/20 text-[#01B9C6]",
    Resolved: "bg-green-100 text-green-600",
    Pending: "bg-yellow-100 text-yellow-600",
    Urgent: "bg-red-100 text-red-600",
    Closed: "bg-gray-100 text-gray-600",
  };

  const priorityColors = {
    Low: "bg-gray-100 text-gray-600",
    Normal: "bg-toucanBlue text-blue-600",
    High: "bg-orange-100 text-orange-600",
    Urgent: "bg-red-100 text-red-600",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Ticket #{ticket.ticketCode}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{ticket.subject}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Status & Priority */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span
              className={`text-xs px-3 py-1 rounded-full font-semibold ${
                statusColors[ticket.status] || "bg-gray-100 text-gray-600"
              }`}
            >
              {ticket.status}
            </span>
            <span
              className={`text-xs px-3 py-1 rounded-full font-semibold ${
                priorityColors[ticket.priority] || "bg-gray-100 text-gray-600"
              }`}
            >
              {ticket.priority} Priority
            </span>
            <span className="text-xs px-3 py-1 rounded-full font-semibold bg-gray-100 text-gray-600">
              {ticket.category}
            </span>
          </div>

          {/* Details */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Description
            </h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {ticket.description}
            </p>
          </div>

          {/* Created Date */}
          <div className="text-xs text-gray-500 mb-4">
            Created: {formatDateTime(ticket.createdAt)}
          </div>

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Attachments
              </h3>
              <div className="space-y-2">
                {ticket.attachments.map((attachment, index) => (
                  <a
                    key={index}
                    href={attachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-200 transition"
                  >
                    <FiPaperclip className="text-[#01B9C6]" />
                    <span className="flex-1 truncate">
                      {attachment.fileName}
                    </span>
                    <FiDownload className="text-gray-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Comments / Conversation */}
          {ticket.comments && ticket.comments.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Conversation
              </h3>
              <div className="space-y-4">
                {ticket.comments.map((comment) => (
                  <div
                    key={comment.commentId}
                    className={`p-3 rounded-lg ${
                      comment.authorRole === "Admin"
                        ? "bg-blue-50 border border-blue-100"
                        : "bg-gray-50 border border-gray-100"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {comment.authorName}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            comment.authorRole === "Admin"
                              ? "bg-blue-100 text-blue-600"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {comment.authorRole}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDateTime(comment.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.message}</p>

                    {/* Comment Attachments */}
                    {comment.attachments && comment.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {comment.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={att.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100"
                          >
                            <FiPaperclip size={12} />
                            {att.fileName}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
